import { useCallback, useEffect, useState } from "react";
import { usePanelCall } from "../hooks/usePanelCall";
import { IconMic, IconInfo, IconAlertCircle } from "./ui/icons";

type ContactOption = {
  id: string;
  phoneRaw: string;
  firstName: string;
  lastName: string;
};

type TrunkOption = {
  id: string;
  slug: string;
  label: string;
  number: string;
};

function statusLabel(status: string): string {
  switch (status) {
    case "connecting":
      return "Łączę...";
    case "live":
      return "Na żywo";
    case "ended":
      return "Rozłączono";
    case "error":
      return "Błąd połączenia";
    default:
      return "Gotowe do połączenia";
  }
}

function contactLabel(c: ContactOption): string {
  const name = `${c.firstName} ${c.lastName}`.replace(/\s+/g, " ").trim();
  return name ? `${name} — ${c.phoneRaw}` : c.phoneRaw;
}

export function PanelCallPanel() {
  const { status, error, transcript, muted, start, hangup, toggleMute } = usePanelCall();
  const live = status === "live" || status === "connecting";
  const connecting = status === "connecting";

  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [trunks, setTrunks] = useState<TrunkOption[]>([]);
  // "" = rozmowa bez numeru (jak dotąd), "__wlasny" = numer wpisany ręcznie.
  const [pick, setPick] = useState("");
  const [customNumber, setCustomNumber] = useState("");
  // "" = wbudowane zachowanie domyślne, inaczej slug numeru, którego ustawienia udajemy.
  const [trunkPick, setTrunkPick] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/admin/api/contacts", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as { contacts?: ContactOption[] };
        if (!cancelled) setContacts(data.contacts ?? []);
      } catch {
        /* lista kontaktów to wygoda, nie warunek rozmowy */
      }
    })();
    void (async () => {
      try {
        const res = await fetch("/admin/api/sip-trunks", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as { trunks?: TrunkOption[] };
        if (!cancelled) setTrunks(data.trunks ?? []);
      } catch {
        /* brak listy numerów = test na wbudowanym zachowaniu, rozmowa i tak ruszy */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const callerNumber = pick === "__wlasny" ? customNumber : pick;

  const onStart = useCallback(() => {
    void start(callerNumber, trunkPick);
  }, [start, callerNumber, trunkPick]);

  return (
    <div>
      {/* Karta 1: opis + info (nad hero) */}
      <div className="panel">
        <h3>Rozmowa z asystentką (przez przeglądarkę)</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Zadzwoń do asystentki bez telefonu - ten sam tor rozmowy co połączenie SIP, tylko z pominięciem
          Asteriska. Pozwala testować na żywo przerywanie i prompty. Rozmowa zapisuje się w zakładce
          Transkrypty jako źródło „panel".
        </p>
        <div className="banner banner--info" style={{ marginTop: 12 }}>
          <IconInfo size={18} />
          <span>Użyj słuchawek - bez nich dźwięk z głośnika wpada do mikrofonu i może przerywać asystentkę.</span>
        </div>

        <label className="admin-field-block" style={{ marginTop: 16 }}>
          <div>Zadzwoń jako</div>
          <p className="muted" style={{ margin: "4px 0 8px" }}>
            Asystentka potraktuje rozmowę tak, jakby dzwonił ten numer — z jego imieniem, instrukcją
            i kontekstem z CRM. Zostaw „nieznany numer", żeby sprawdzić zachowanie wobec kogoś spoza
            kontaktów.
          </p>
          <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={live}>
            <option value="">Nieznany numer (bez rozpoznawania)</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.phoneRaw}>
                {contactLabel(c)}
              </option>
            ))}
            <option value="__wlasny">Inny numer…</option>
          </select>
        </label>

        <label className="admin-field-block" style={{ marginTop: 12 }}>
          <div>Zachowuj się jak numer</div>
          <p className="muted" style={{ margin: "4px 0 8px" }}>
            Asystentka weźmie instrukcję początkową, tryb powitania, sekcję „kto dzwoni"
            i dodatkową instrukcję z wybranego numeru — dzięki temu test brzmi tak samo jak
            prawdziwa rozmowa. Bez wyboru zostają ustawienia wbudowane.
          </p>
          <select value={trunkPick} onChange={(e) => setTrunkPick(e.target.value)} disabled={live}>
            <option value="">Zachowanie wbudowane (bez numeru)</option>
            {trunks.map((t) => (
              <option key={t.id} value={t.slug}>
                {t.label} — {t.number}
              </option>
            ))}
          </select>
        </label>

        {pick === "__wlasny" && (
          <label className="admin-field-block" style={{ marginTop: 12 }}>
            <div>Numer dzwoniącego</div>
            <input
              type="text"
              value={customNumber}
              onChange={(e) => setCustomNumber(e.target.value)}
              placeholder="np. 509 891 745"
              maxLength={40}
              disabled={live}
            />
          </label>
        )}
      </div>

      {/* Hero / kontrolki NA WASHU - samodzielny hero pełnej szerokości nie wchodzi w kartę (B-6, L-8) */}
      <div className="hero-row" style={{ flexDirection: "column", alignItems: "center", gap: 12 }}>
        {!live ? (
          <button type="button" className={`btn-hero${connecting ? " is-loading" : ""}`} onClick={onStart}>
            <IconMic size={18} />
            <span className="btn-hero__label">Zadzwoń do asystentki</span>
          </button>
        ) : (
          <div className="row" style={{ justifyContent: "center", gap: 12 }}>
            <span className="status-pill status-pill--ok">
              {status === "live" ? <span className="live-dot" /> : <span className="status-pill__dot" />}
              {statusLabel(status)}
            </span>
            <button type="button" className="btn-danger" onClick={hangup}>
              Rozłącz
            </button>
            <button type="button" className="btn-secondary" onClick={toggleMute}>
              {muted ? "Wyłącz wyciszenie" : "Wycisz mikrofon"}
            </button>
          </div>
        )}
        {!live && status !== "idle" && (
          <p className="muted" style={{ margin: 0 }}>{statusLabel(status)}</p>
        )}
        {error && (
          <p className="field-error" style={{ margin: 0, justifyContent: "center" }}>
            <IconAlertCircle size={14} />
            {error}
          </p>
        )}
      </div>

      {/* Karta 2: transkrypt (pod hero) */}
      <div className="panel">
        <h3>Transkrypt rozmowy</h3>
        <div className="panel-call-transcript" style={{ marginTop: 12 }}>
          {transcript.length === 0 ? (
            <p className="muted" style={{ margin: "auto", textAlign: "center" }}>
              {live ? "Mów śmiało - transkrypt pojawi się tutaj." : "Transkrypt rozmowy pojawi się tutaj."}
            </p>
          ) : (
            transcript.map((line: { speaker: string; text: string }, i: number) => (
              <div key={i} className={`panel-call-line panel-call-line--${line.speaker}`}>
                <strong>{line.speaker === "user" ? "Ty" : "Asystentka"}</strong>
                {line.text}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
